/**
 * LocalStorage persistence for:
 *   • Settings  — UI preferences (checkbox/timer states), survive page refresh
 *   • Session   — current query + route + POIs, wiped on new query
 */

import type { LatLng, PoiKind } from "../types";
import type { ParsedPoi } from "../poi/types";

// ─── Settings ──────────────────────────────────────────────────────────────────

export interface Settings {
  minHours:          number;
  maxHours:          number;
  radiusKm:          number;
  showSearchArea:    boolean;
  showOnlySelected:  boolean;
  includeToilets:    boolean;
  poiDetourMaxMin:   number;  // 0 = no filter; >0 hides reachable POIs with detourCostS > N minutes
}

const SETTINGS_KEY = "crp_settings";

export function loadSettings(): Settings | null {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as Settings) : null;
  } catch {
    return null;
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // quota exceeded or private browsing — ignore
  }
}

export function clearSettings(): void {
  try {
    localStorage.removeItem(SETTINGS_KEY);
  } catch {
    // ignore
  }
}

// ─── Session ───────────────────────────────────────────────────────────────────

// ParsedPoi stripped of non-serialisable / closure-captured refs
export interface SerializedPoi {
  id:           string;
  osmType:      "node" | "way";
  lat:          number;
  lng:          number;
  name:         string;
  amenity:      string;
  tags:         Record<string, string>;
  kind:         PoiKind;
  routeDistM:   number;
  detourArrivalS:    number | null;
  detourCostS:       number | null;
  detourReachable:   boolean;
}

export function serializePoi(poi: ParsedPoi): SerializedPoi {
  return {
    id:         poi.id,
    osmType:    poi.osmType,
    lat:        poi.lat,
    lng:        poi.lng,
    name:       poi.name,
    amenity:    poi.amenity,
    tags:       poi.tags,
    kind:       poi.kind,
    routeDistM: poi.routeDistM,
    detourArrivalS:  poi.detour?.arrivalS    ?? null,
    detourCostS:      poi.detour?.detourCostS ?? null,
    detourReachable:  poi.detour?.reachable   ?? false,
  };
}

export function deserializePoi(s: SerializedPoi): ParsedPoi {
  return {
    id:         s.id,
    osmType:    s.osmType ?? "node",
    lat:        s.lat,
    lng:        s.lng,
    name:       s.name,
    amenity:    s.amenity,
    tags:       s.tags,
    kind:       s.kind,
    routeDistM: s.routeDistM,
    detour: (s.detourReachable || s.detourArrivalS !== null || s.detourCostS !== null)
      ? { arrivalS: s.detourArrivalS ?? 0, detourCostS: s.detourCostS ?? 0, reachable: s.detourReachable }
      : undefined,
  };
}

export interface Session {
  startName:      string;
  destName:       string;
  startLat:       number;
  startLng:       number;
  destLat:        number;
  destLng:        number;
  routeGeometry:  LatLng[];
  routeDurationS:  number;
  routeDistM:      number;
  segmentTimes:    number[];   // per-segment durations from OSRM steps
  pois:           SerializedPoi[];
  selectedPoiIds?: string[];  // deprecated — POI selection now persisted by selection.ts
}

const SESSION_KEY = "crp_session";

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

/**
 * Save session. selectedPoiIds is optional — POI selection is now persisted
 * separately by selection.ts (crp_poi_selection key).
 */
export function saveSession(s: Omit<Session, "selectedPoiIds"> & { selectedPoiIds?: string[] }): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export function hasSession(): boolean {
  return localStorage.getItem(SESSION_KEY) !== null;
}
